import CarPeritajeReportEntity, { PeritajeSource } from "../../../../../domain/entities/CarPeritajeReportEntity";
import CarPeritajeReportImplDto from "../../../../dto/impl/CarPeritajeReportImplDto";
import GetCarAutomasByPlateApiImpl from "../../../automas/GetCarAutomasByPlateApiImpl";
import GetColserAutoByPlacaApiImpl from "../../../colserauto/GetColserAutoByPlacaApiImpl";
import HostApi from "../../../../settings/HostApi";

/**
 * Consultas por placa que se hacen en paralelo. El reporte dispara hasta dos peticiones por
 * vehículo sobre un inventario de un par de cientos, así que se limita para no saturar al backend.
 */
const CONCURRENCY = 3;

/**
 * Reintentos ante fallos del servicio (p. ej. Colserauto inalcanzable desde el backend). Los 404
 * ("no tiene peritaje") no se reintentan. Esperas crecientes para darle tiempo a recuperarse.
 */
const RETRY_DELAYS_MS = [2000, 5000, 10000];

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

const withRetry = async <T,>(query: () => Promise<T>): Promise<T> => {
    for (let attempt = 0; ; attempt++) {
        try {
            return await query();
        } catch (error) {
            if (attempt >= RETRY_DELAYS_MS.length) throw error;
            await sleep(RETRY_DELAYS_MS[attempt]);
        }
    }
};

/** Ejecuta `worker` sobre `items` con un pool de tamaño fijo, conservando el orden de entrada. */
const mapWithPool = async <T, R>(
    items: T[],
    worker: (item: T, index: number) => Promise<R>,
    onSettled?: () => void,
): Promise<R[]> => {
    const results = new Array<R>(items.length);
    let next = 0;

    const run = async (): Promise<void> => {
        while (next < items.length) {
            const index = next++;
            results[index] = await worker(items[index], index);
            onSettled?.();
        }
    };

    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, items.length) }, run));
    return results;
};

/** Recorre todas las páginas de `/v2/filter-cars/` sin filtros: son los vehículos activos. */
const fetchActiveCars = async (): Promise<CarPeritajeReportEntity[]> => {
    const firstPage = await HostApi.post("/v2/filter-cars/?page=1", {});
    const pages = Number(firstPage?.num_pages ?? 1);

    const rest = await Promise.all(
        Array.from({ length: Math.max(pages - 1, 0) }, (_, i) => HostApi.post(`/v2/filter-cars/?page=${i + 2}`, {})),
    );

    // El backend pagina sobre un inventario vivo, así que un mismo vehículo puede repetirse
    // entre páginas: se deduplica por id.
    const byId = new Map<string, CarPeritajeReportEntity>();
    [firstPage, ...rest].forEach((page: any) => {
        (page?.results ?? []).forEach((json: any) => {
            const car = CarPeritajeReportImplDto.fromJson(json);
            byId.set(car.id, car);
        });
    });

    return Array.from(byId.values());
};

/**
 * Resuelve el peritaje de un vehículo con la misma prioridad que `ModalPeritajeVehiculo`:
 * PDF propio de Automás → Automás por placa → PDF de Colserauto por id → Colserauto por placa.
 *
 * Las consultas por placa solo se hacen cuando hacen falta, para no pedirle al backend lo que
 * el vehículo ya trae resuelto.
 */
const resolvePeritaje = async (car: CarPeritajeReportEntity): Promise<CarPeritajeReportEntity> => {
    if (car.hasAutomasPdf) return { ...car, source: "automasPdf" };

    try {
        const hasAutomasApi = car.plate
            ? (await withRetry(() => GetCarAutomasByPlateApiImpl(car.plate, { strict: true }))) != null
            : false;
        if (hasAutomasApi) return { ...car, hasAutomasApi, source: "automasApi" };

        if (car.hasColserautoId) return { ...car, hasAutomasApi, source: "colserautoPdf" };

        const hasColserautoApi = car.plate
            ? (await withRetry(() => GetColserAutoByPlacaApiImpl(car.plate, { strict: true }))) != null
            : false;
        const source: PeritajeSource | null = hasColserautoApi ? "colserautoApi" : null;

        return { ...car, hasAutomasApi, hasColserautoApi, source };
    } catch {
        // Agotó los reintentos: no se sabe si tiene peritaje, así que no se afirma que no lo tiene.
        return { ...car, source: null, checkFailed: true };
    }
};

const GetActiveCarsPeritajeReportApiImpl = async (
    onProgress?: (done: number, total: number) => void,
): Promise<CarPeritajeReportEntity[]> => {
    const cars = await fetchActiveCars();

    let done = 0;
    onProgress?.(0, cars.length);

    const rows = await mapWithPool(cars, resolvePeritaje, () => {
        done += 1;
        onProgress?.(done, cars.length);
    });

    // Primero los que no tienen peritaje: son los que hay que accionar.
    return rows.sort(
        (a, b) =>
            Number(a.source != null) - Number(b.source != null) ||
            a.brand.localeCompare(b.brand) ||
            a.line.localeCompare(b.line),
    );
};

export default GetActiveCarsPeritajeReportApiImpl;
